import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import loadUserDetails from "@tCooneyBrothers/Handlers/LoadUserDetails";
import CustomApplicationState from "@tCooneyBrothers/Store/CustomApplicationState";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: CustomApplicationState) => ({
    companyName: state.components.contactUsForm.fieldValues["companyName"],
    isAuthenticated: state.context?.session.isAuthenticated,
    signedInCompany: state.cbData.userDetails.value,
});

const mapDispatchToProps = {
    setFieldValue,
    loadUserDetails,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CompanyNameStyles {
    companyNameTextField?: TextFieldPresentationProps;
}

export const companyNameStyles: CompanyNameStyles = {};

const styles = companyNameStyles;

const CompanyName: React.FC<Props> = ({
    fields,
    companyName,
    isAuthenticated,
    signedInCompany,
    loadUserDetails,
    setFieldValue,
}) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [userCompany, setUserCompany] = React.useState(companyName);
    const [companyNameErrorMessage, setCompanyNameErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.lastName = validateCompanyName;
        if (typeof userCompany !== "undefined") {
            validateCompanyName();
        }
    }, [userCompany]);

    React.useEffect(() => {
        if (isAuthenticated && signedInCompany && signedInCompany.length > 0) {
            setFieldValue({ key: "companyName", value: signedInCompany[0].companyName });
            setUserCompany(signedInCompany[0].companyName);
        }
    }, [signedInCompany]);

    React.useEffect(() => {
        if (isAuthenticated) {
            loadUserDetails();
            validateCompanyName();
        }
    }, []);

    React.useEffect(() => {
        contactFormContext.validators.lastName = validateCompanyName;
    }, [isRequired]);

    const validateCompanyName = () => {
        const errorMessage = isRequired && !userCompany ? (siteMessage("Field_Required", label) as string) : "";
        setCompanyNameErrorMessage(errorMessage);
        return !errorMessage;
    };

    const companyNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "companyName", value: event.target.value });
        setUserCompany(event.target.value);
    };

    return (
        <TextField
            {...styles.companyNameTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={userCompany || ""}
            error={companyNameErrorMessage}
            onChange={companyNameChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CompanyName),
    definition: {
        group: "Contact Us",
        icon: "FormField",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Company Name",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Company Name",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
